import { BlueprintInstanceType } from '../shared.enums';
import { CountertopEdge } from '../countertops/enums';
import { countertopMaterialData } from './countertop-material.data';
import { KitchenDesignBase } from '../kitchen-design-base';
import { Publish } from '../../decorators';
import { COUNTERTOP_DEFAULT_KEY, COUNTERTOP_INVALID_KEY, COUNTERTOP_NONE_KEY } from './constants';
import { setCountertopThicknessCfn } from './functions';
import { Size } from '../../types';

import { CountertopMaterialDataIn, CountertopMaterialDataOut, CountertopMaterialOptions } from './types';

import countertopMap from '../data/countertop-asset.map.json';

/**
 * A class for handling Countertop material selection and persistence
*/
export class CountertopMaterial extends KitchenDesignBase<CountertopMaterialDataIn> {
	/** This allows the class instance to be updated when syncTo is used */
	#override: boolean;
	/** A friendly name for the selected countertop. */
	#displayName: string;
	/** Code to use when assembling or decoding sku's containing a countertop material identifier */
	#code: string;
	/** is this asset available for use */
	#enabled: boolean;
	/** What type of edge is on the countertop. */
	#edge: CountertopEdge;
	/** is the material granite */
	#backsplashSku: string | null = null;
	#isGranite: boolean;
	/** Maps to a key in the countertop data file */
	#key: string;
	/** key prior to the most recent setMaterial() call; null on first init */
	#previousKey: string | null = null;
	/** Raw key that failed to resolve — preserved so serialize() never overwrites persisted data with the fallback */
	#unresolvedKey: string | null = null;
	/** scale to map texture */
	#scale: Size;
	/** object to sync values to when !override */
	#syncTo: CountertopMaterial | null;
	/** texture filename */
	#texture: string;
	/** What type of countertop; i.e Concrete, Granite */
	#type: string;
	/** How thick is the countertop. There are minimum and maximum values */
	#thickness: number;

	/**
	 * The constructor of the CountertopMaterial class.
	 *
	 * @param options is of generic type ClassOptions for CountertioMaterialData with a specified ParentType
	 */
	constructor (options: CountertopMaterialOptions = {}) {
		super(BlueprintInstanceType.CountertopMaterial, options);

		const data = countertopMaterialData(options.dataIn);
		this.#syncTo = options.syncTo ?? null;
		// always true when syncTo isn't provided
		this.#override = this.#syncTo === null ? true : data.override;

		if (this.#syncTo !== null && !this.#override) {
			this.setMaterial(this.#syncTo.key);
			this.#thickness = this.#syncTo.thickness;
		} else {
			this.setMaterial(data.key);
			this.#thickness = data.thickness;
		}

		// console.log('CountertopMaterial->constructor', this.#syncTo, this.#syncTo?.key, this.#override);
		this.setInitialized();
	}

	get displayName (): string { return this.#displayName; }
	get code (): string { return this.#code; }
	get enabled (): boolean { return this.#enabled }
	get edge (): null | string { return this.#edge; }
	get backsplashSku (): string | null { return this.#backsplashSku; }

	get isGranite (): boolean { return this.#isGranite; }
	get key (): string { return this.#key; }
	/** Key before the most recent setMaterial() call — null before the first switch. Consumers use this to detect material-family transitions (e.g. MOK countertop ↔ non-MOK). */
	get previousKey (): string | null { return this.#previousKey; }
	get scale (): Size { return this.#scale; }
	get texture (): string { return this.#texture }
	get type (): string { return this.#type }

	get thickness (): number { return this.#thickness }
	@Publish('countertop_thickness', 'updated')
	set thickness (t: number) {
		this.#thickness = setCountertopThicknessCfn(this, t, this.context);
	}


	get override (): boolean { return this.#override }
	/**
	 * Setter override resets material to kitchen material when override is disabled
	 */
	@Publish('countertop_material_override', 'updated')
	set override (override: boolean) {
		if (this.#override && !override) {
			// reset value
			this.reset();
		}
		this.#override = override;
	}

	/**
	 * Public method resets this countertop material instance to match kitchen countertop
	 */
	@Publish('countertop_material', 'reset')
	public reset (): void {
		if (this.refMap.kitchen) this.setMaterial(this.refMap.kitchen.material.countertop.key);
	}

	/**
	 * A public method. Called to select a new countertop from the datafile.
	 */
	@Publish('countertop_material', 'updated')
	public setMaterial (key: string): boolean {
		// if there is a kitchen parent then override must be set to change
		if (!this.initialized || this.#override) {
			// Legacy 'non-rta' key folded into 'none' so existing saved designs migrate cleanly.
			let useKey = key === 'non-rta' ? COUNTERTOP_NONE_KEY : key;
			let ct = countertopMap[useKey] ?? null;
			let isSet = true;

			if (!ct) {
				this.#unresolvedKey = key;
				useKey = COUNTERTOP_INVALID_KEY;
				ct = countertopMap[useKey];
				isSet = false;
			} else {
				this.#unresolvedKey = null;
			}

			this.#displayName = ct.displayName ?? '';
			this.#code = ct.code ?? '';
			this.#edge = ct.edge;
			this.#enabled = ct.enabled;
			this.#backsplashSku = ct.backsplashSku ?? null;
			this.#isGranite = ct.isGranite;
			this.#previousKey = this.#key ?? null;
			this.#key = useKey;
			this.#scale = ct.scale;
			this.#texture = ct.texture;
			this.#type = isSet
				? (useKey === COUNTERTOP_NONE_KEY
					? null
					: ct.isGranite ? 'granite' : 'concrete')
				: null;

			// if (this.#syncTo === null) console.log('setMaterial(inside)', useKey, ct, this);
			return isSet;
		}

		return false;
	}

	/**
	 * a Public method. Used to create an object of CountertopMaterialData for persisting the class.
	 */
	public serialize (): CountertopMaterialDataOut {
		const record: CountertopMaterialDataOut = {
			key: this.#unresolvedKey ?? this.#key,
			thickness: this.#thickness
		};

		if (this.#syncTo !== null && this.#override) record.override = true;

		return record;
	}

	/**
	 * Returns all enabled countertop material options for programmatic or AI access.
	 */
	static getOptions (): CountertopMaterialOptionsResult {
		const options: CountertopMaterialOptionItem[] = [];

		for (const [key, asset] of Object.entries(countertopMap)) {
			if (asset.enabled && asset.texture) {
				options.push({
					key,
					displayName: asset.displayName,
					isGranite: asset.isGranite,
					edge: asset.edge,
					code: asset.code,
					thumbnailUrl: asset.texture
				});
			}
		}

		return {
			options,
			defaultKey: COUNTERTOP_DEFAULT_KEY
		};
	}
}

export interface CountertopMaterialOptionItem {
	key: string;
	displayName: string;
	isGranite: boolean;
	edge: string | null;
	code: string;
	thumbnailUrl: string;
}

export interface CountertopMaterialOptionsResult {
	options: CountertopMaterialOptionItem[];
	defaultKey: string;
}
